/**
 * useStudentTutorConversationState — les gestes de la conversation avec un tuteur.
 *
 * Ce qu'il possède (réducteur pur, exporté et testé) : le brouillon, la boîte d'envoi (ce que
 * l'élève a envoyé d'ici, en cours, parti ou non parti), le message dont « Copier » est ouvert, la
 * visionneuse, et le bandeau « Ce fichier n'a pas pu être envoyé ».
 *
 * Les gestes, ports de la branche élève de `common/messenger/CommonChat.js` :
 *  - envoyer un texte (`postMessages`, `{ message, temporary_id: "message" }`) ;
 *  - joindre une photo de l'appareil (la route legacy `camera`, `formId: "chat"`, dont la photo est
 *    envoyée au retour), de la galerie (mobile seulement, comme aujourd'hui), ou un document pdf ou
 *    image (`postImage` / `postDocument`, un formulaire `temporary_id` + la pièce) — sans aperçu ;
 *  - « Renvoyer » un message qui n'est pas parti (nouveau : l'ancien fil le laissait comme parti,
 *    DECISIONS du 13/10) ; un refus du serveur dit son motif sous la bulle, comme l'ancienne boîte
 *    « Erreur », et ne propose plus « Renvoyer » quand il est définitif (`messengerFailure`) ;
 *  - toucher une photo ou un document : l'activité que le tuteur en a tirée s'ouvre
 *    (`getActivityFromChat`), sinon la visionneuse ;
 *  - appui long sur un texte : « Copier » ;
 *  - un lien d'un message touché : l'adresse web ou l'e-mail s'ouvre, un numéro propose d'appeler
 *    ou d'écrire un SMS (`conversationLinks`, ce que faisait `MessageText` de l'ancien fil) ;
 *  - « Message vocal » : l'enregistreur s'ouvre (`recorderOpen`) ; le vocal fini part par la file
 *    d'envoi des vocaux (`messengerVoiceOutbox`, voir `useStudentTutorConversation`).
 */
import { useIsFocused, useNavigation } from "@react-navigation/native"
import * as Clipboard from "expo-clipboard"
import * as DocumentPicker from "expo-document-picker"
import * as ImagePicker from "expo-image-picker"
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { setCurrentActivity } from "../../../../../../data/redux/actions/activity"
import { clearLastPhoto } from "../../../../../../data/redux/actions/camera"
import { messengerService } from "../../../../../../data/services/messengerService"
import { type ComposerAttachSource } from "../../../../../../design-system/student"
import {
    messengerFailure,
    type MessengerFailure,
} from "../../../../adapters/student/Messaging/studentConversationAdapter"
import { type ConversationOutboxEntry } from "../../../../view-models/student/Messaging/studentTutorConversationViewModel"
import { useApiMutation } from "../../../hooks/useApiMutation"
import {
    type UIConversationMessage,
    type UIMessageAttachment,
    type UIMessageLink,
} from "../../../types/studentConversation"
import { linkHref, phoneHref, type PhoneAction } from "./conversationLinks"
import { openConversationLink } from "./openConversationLink"
import { attachmentForm, documentPart, imagePart, isPdfFile, uploadPart, type FilePart } from "./conversationFiles"
import { useMessengerPost, type MessengerPostOutcome } from "./useMessengerPost"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"

export type ConversationViewer =
    { kind: "image"; images: { uri: string }[]; index: number } | { kind: "pdf"; uri: string }

type State = {
    draft: string
    outbox: ConversationOutboxEntry[]
    copyMessage: UIConversationMessage | null
    viewer: ConversationViewer | null
    unsentNotice: boolean
    /** L'enregistreur d'un message vocal est ouvert. */
    recorderOpen: boolean
    /** Le numéro touché dans un message : la feuille « Appeler / Envoyer un SMS » est ouverte. */
    phone: string | null
}

type Action =
    | { type: "CHANGE_DRAFT"; text: string }
    | { type: "QUEUE"; entry: ConversationOutboxEntry; clearDraft?: boolean }
    | { type: "SENT"; localId: string; server: ConversationOutboxEntry["server"] }
    | { type: "FAILED"; localId: string; failure?: MessengerFailure }
    | { type: "RETRY"; localId: string }
    | { type: "OPEN_COPY"; message: UIConversationMessage }
    | { type: "CLOSE_COPY" }
    | { type: "OPEN_VIEWER"; viewer: ConversationViewer }
    | { type: "CLOSE_VIEWER" }
    | { type: "SHOW_UNSENT" }
    | { type: "HIDE_UNSENT" }
    | { type: "OPEN_RECORDER" }
    | { type: "CLOSE_RECORDER" }
    | { type: "OPEN_PHONE"; phone: string }
    | { type: "CLOSE_PHONE" }
    | { type: "RESET" }

export const initialConversationState: State = {
    draft: "",
    outbox: [],
    copyMessage: null,
    viewer: null,
    unsentNotice: false,
    recorderOpen: false,
    phone: null,
}

const withStatus = (outbox: ConversationOutboxEntry[], localId: string, patch: Partial<ConversationOutboxEntry>) =>
    outbox.map((entry) => (entry.localId === localId ? { ...entry, ...patch } : entry))

export function conversationReducer(state: State, action: Action): State {
    switch (action.type) {
        case "CHANGE_DRAFT":
            return { ...state, draft: action.text }
        case "QUEUE":
            return {
                ...state,
                outbox: [...state.outbox, action.entry],
                draft: action.clearDraft ? "" : state.draft,
            }
        case "SENT":
            return {
                ...state,
                outbox: withStatus(state.outbox, action.localId, {
                    status: "sent",
                    ...(action.server != null ? { server: action.server } : {}),
                }),
            }
        case "FAILED":
            return {
                ...state,
                outbox: withStatus(state.outbox, action.localId, { status: "failed", failure: action.failure }),
            }
        case "RETRY":
            return {
                ...state,
                outbox: withStatus(state.outbox, action.localId, { status: "sending", failure: undefined }),
            }
        case "OPEN_COPY":
            return { ...state, copyMessage: action.message }
        case "CLOSE_COPY":
            return { ...state, copyMessage: null }
        case "OPEN_VIEWER":
            return { ...state, viewer: action.viewer }
        case "CLOSE_VIEWER":
            return { ...state, viewer: null }
        case "SHOW_UNSENT":
            return { ...state, unsentNotice: true }
        case "HIDE_UNSENT":
            return { ...state, unsentNotice: false }
        case "OPEN_RECORDER":
            return state.recorderOpen ? state : { ...state, recorderOpen: true }
        case "CLOSE_RECORDER":
            return state.recorderOpen ? { ...state, recorderOpen: false } : state
        case "OPEN_PHONE":
            return { ...state, phone: action.phone }
        case "CLOSE_PHONE":
            return state.phone == null ? state : { ...state, phone: null }
        case "RESET":
            return initialConversationState
        default:
            return state
    }
}

/** Le message qu'on vient de toucher → sa visionneuse : toutes les photos du fil, ou le document. */
export function viewerFor(
    message: UIConversationMessage,
    messages: UIConversationMessage[]
): ConversationViewer | null {
    const attachment = message.attachment
    if (attachment?.kind === "document") return { kind: "pdf", uri: attachment.uri }
    if (attachment?.kind !== "image") return null
    const images = messages.filter((m) => m.attachment?.kind === "image")
    const index = images.findIndex((m) => m.id === message.id)
    return {
        kind: "image",
        images: images.map((m) => ({ uri: (m.attachment as { uri: string }).uri })),
        index: Math.max(0, index),
    }
}

/**
 * Les sources d'aujourd'hui : l'appareil photo, la galerie sur mobile seulement, un document, et
 * le message vocal (le micro, en dernier, comme dans l'ancien fil).
 */
export const CONVERSATION_ATTACH_SOURCES: ComposerAttachSource[] =
    Platform.OS === "web" ? ["camera", "document", "voice"] : ["camera", "gallery", "document", "voice"]

/** Le `formId` que l'appareil photo legacy pose sur la photo prise pour la messagerie. */
const CAMERA_FORM_ID = "chat"
const LOCAL_PREFIX = "local-"

let localSequence = 0
const newLocalId = () => `${LOCAL_PREFIX}${Date.now()}-${++localSequence}`

type Field = "image" | "document"
type Navigation = {
    push: (route: string, params?: object) => void
    replace: (route: string, params?: object) => void
    navigate: (route: string, params?: object) => void
}

export type UseStudentTutorConversationStateArgs = {
    threadId: string | null
    /**
     * Monté dans un volet de « Messages » (et non sur sa route) : l'activité ouverte depuis une
     * photo est POUSSÉE ; sur la route `messenger`, elle la REMPLACE, comme aujourd'hui.
     */
    embedded: boolean
    /**
     * Un fichier choisi (appareil, galerie, document) ne part pas : il est rendu ici. Le tuteur le
     * montre d'abord (« Nouveau fichier » : créer une activité ou l'envoyer, `MediaPreviewModal`).
     * Absent : il part aussitôt, comme pour l'élève. `actions.sendHeldFile` l'envoie ensuite.
     */
    holdNewFile?: (file: HeldConversationFile) => void
    /**
     * Toucher une photo ou un document : l'écran décide (le tuteur ouvre l'activité qui en est née, ou
     * son aperçu). Absent : `getActivityFromChat`, puis la visionneuse — le geste de l'élève.
     */
    onOpenAttachment?: (message: UIConversationMessage, messages: UIConversationMessage[]) => void
}

/** Un fichier choisi dans le composeur, retenu avant de partir (`holdNewFile`). */
export type HeldConversationFile = {
    kind: "image" | "document"
    uri: string
    name?: string | null
    mimeType?: string | null
}

export function useStudentTutorConversationState(args: UseStudentTutorConversationStateArgs) {
    const { threadId, embedded, holdNewFile, onOpenAttachment } = args
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation
    const isFocused = useIsFocused()
    const [state, dispatchUi] = useReducer(conversationReducer, initialConversationState)

    // Le nom qui signe une photo envoyée (`Masteur_Image_<nom>_<date>.png`) : celui du compte qui écrit
    // (`CommonChat.onSendImage`). Pour l'élève et en mode parent, c'est l'élève affiché ; pour le tuteur, lui.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const displayName = useSelector((s: any) => selectAccount(s)?.display_name as string | undefined)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastPhoto = useSelector((s: any) => s?.camera?.lastPhoto as { uri?: string } | null | undefined)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastPhotoFormId = useSelector((s: any) => s?.camera?.formId as string | null | undefined)

    /** La pièce de chaque envoi, gardée pour « Renvoyer ». */
    const partsRef = useRef(new Map<string, { field: Field; part: FilePart | File }>())

    // Une autre conversation (le volet de « Messages » change de tuteur) : rien ne se garde.
    useEffect(() => {
        partsRef.current.clear()
        dispatchUi({ type: "RESET" })
    }, [threadId])

    const onDone = useCallback((localId: string, outcome: MessengerPostOutcome) => {
        if (outcome.ok) {
            partsRef.current.delete(localId)
            dispatchUi({ type: "SENT", localId, server: outcome.server ?? undefined })
        } else {
            // Une pièce gardée pour « Renvoyer » : c'est un fichier, et un refus sans motif dit la phrase des fichiers.
            const failure = messengerFailure("error" in outcome ? outcome.error : null, partsRef.current.has(localId))
            if (!failure.retryable) partsRef.current.delete(localId)
            dispatchUi({ type: "FAILED", localId, failure })
        }
    }, [])

    const text = useMessengerPost(messengerService.postMessages, onDone)
    const image = useMessengerPost(messengerService.postImage, onDone)
    const document = useMessengerPost(messengerService.postDocument, onDone)
    const postFor = (field: Field) => (field === "image" ? image : document)

    const send = useCallback(() => {
        const message = state.draft.trim()
        if (threadId == null || message === "" || text.busy) return
        const localId = newLocalId()
        dispatchUi({
            type: "QUEUE",
            entry: { localId, sentAt: new Date().toISOString(), text: message, status: "sending" },
            clearDraft: true,
        })
        if (!text.post(localId, threadId, { message, temporary_id: "message" })) {
            dispatchUi({ type: "FAILED", localId })
        }
    }, [state.draft, threadId, text])

    const sendFile = useCallback(
        async (field: Field, part: FilePart, preview: UIMessageAttachment) => {
            if (threadId == null) return
            const uploadable = await uploadPart(part)
            if (uploadable == null) {
                dispatchUi({ type: "SHOW_UNSENT" })
                return
            }
            const localId = newLocalId()
            partsRef.current.set(localId, { field, part: uploadable })
            dispatchUi({
                type: "QUEUE",
                entry: { localId, sentAt: new Date().toISOString(), attachment: preview, status: "sending" },
            })
            if (!postFor(field).post(localId, threadId, attachmentForm(field, uploadable))) {
                dispatchUi({ type: "FAILED", localId })
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [threadId, image.post, document.post]
    )

    const sendImage = useCallback(
        (uri: string) => void sendFile("image", imagePart(uri, displayName), { kind: "image", uri }),
        [sendFile, displayName]
    )

    const holdRef = useRef(holdNewFile)
    holdRef.current = holdNewFile
    /** Une photo choisie : retenue si l'écran le demande, sinon envoyée. */
    const takeImage = useCallback(
        (file: { uri: string; name?: string | null; mimeType?: string | null }) => {
            if (holdRef.current != null) holdRef.current({ kind: "image", ...file })
            else sendImage(file.uri)
        },
        [sendImage]
    )

    /** Un fichier retenu (`holdNewFile`), envoyé : la photo comme une photo, le PDF comme un document. */
    const sendHeldFile = useCallback(
        async (file: HeldConversationFile) => {
            if (file.kind === "image") {
                await sendFile("image", imagePart(file.uri, displayName), { kind: "image", uri: file.uri })
                return
            }
            // Le nom retenu est déjà celui de `pickFile` (daté) : il part tel quel, comme dans le legacy.
            const part = file.name ? { uri: file.uri, name: file.name, type: "application/pdf" } : documentPart(file)
            await sendFile("document", part, { kind: "document", uri: file.uri, name: part.name })
        },
        [sendFile, displayName]
    )

    // La photo de l'appareil revient par le magasin (`camera.lastPhoto`, `formId: "chat"`) ; seule la
    // conversation au premier plan la prend : le volet de « Messages » et `messenger` peuvent
    // être montés à la fois. Sans fil ouvert (le volet fermé), elle reste au magasin.
    useEffect(() => {
        if (!isFocused || threadId == null || lastPhotoFormId !== CAMERA_FORM_ID || lastPhoto?.uri == null) return
        const uri = lastPhoto.uri
        dispatch(clearLastPhoto() as never)
        takeImage({ uri })
    }, [dispatch, isFocused, threadId, lastPhoto, lastPhotoFormId, takeImage])

    const retry = useCallback(
        (message: UIConversationMessage) => {
            const entry = state.outbox.find((e) => e.localId === message.id && e.status === "failed")
            if (entry == null || threadId == null || entry.failure?.retryable === false) return
            if (entry.text != null && entry.attachment == null) {
                if (text.post(entry.localId, threadId, { message: entry.text, temporary_id: "message" })) {
                    dispatchUi({ type: "RETRY", localId: entry.localId })
                }
                return
            }
            const kept = partsRef.current.get(entry.localId)
            if (kept == null) return
            if (postFor(kept.field).post(entry.localId, threadId, attachmentForm(kept.field, kept.part))) {
                dispatchUi({ type: "RETRY", localId: entry.localId })
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.outbox, threadId, text.post, image.post, document.post]
    )

    const attach = useCallback(
        async (source: ComposerAttachSource) => {
            if (source === "voice") {
                dispatchUi({ type: "OPEN_RECORDER" })
                return
            }
            if (source === "camera") {
                navigation.navigate("camera", { formId: CAMERA_FORM_ID })
                return
            }
            if (source === "gallery") {
                const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ["images"], quality: 1 })
                const asset = result.canceled ? null : result.assets?.[0]
                if (asset?.uri) takeImage({ uri: asset.uri, name: asset.fileName, mimeType: asset.mimeType })
                return
            }
            if (source === "document") {
                const result = await DocumentPicker.getDocumentAsync({ type: ["application/pdf", "image/*"] })
                const asset = result.canceled ? null : result.assets?.[0]
                if (asset?.uri == null) return
                if (!isPdfFile(asset)) {
                    // Une image choisie comme document part comme une photo (`pickFile` → `onSendImage`).
                    takeImage({ uri: asset.uri, name: asset.name, mimeType: asset.mimeType })
                    return
                }
                const part = documentPart(asset)
                if (holdRef.current != null) {
                    // Le nom du legacy (`pickFile`), que « Envoyer dans le chat » reprendra tel quel.
                    holdRef.current({ kind: "document", uri: asset.uri, name: part.name, mimeType: "application/pdf" })
                    return
                }
                void sendFile("document", part, { kind: "document", uri: asset.uri, name: part.name })
            }
        },
        [navigation, takeImage, sendFile]
    )

    // Toucher une photo ou un document : l'activité que le tuteur en a tirée, sinon la visionneuse.
    const openingRef = useRef<{ message: UIConversationMessage; messages: UIConversationMessage[] } | null>(null)
    const activityFromChat = useApiMutation({
        service: messengerService.getActivityFromChat,
        voice: "student",
        scopeParam: "id",
        onSuccess: (activity) => {
            openingRef.current = null
            const data = activity as { finished_at?: string | null } & Record<string, unknown>
            dispatch(
                setCurrentActivity({
                    activityData: data,
                    progressStatus: data?.finished_at ? "finished" : "ongoing",
                }) as never
            )
            if (embedded) navigation.push("activity")
            else navigation.replace("activity")
        },
        onError: () => {
            const opening = openingRef.current
            openingRef.current = null
            const viewer = opening != null ? viewerFor(opening.message, opening.messages) : null
            if (viewer != null) dispatchUi({ type: "OPEN_VIEWER", viewer })
        },
    })
    const startActivityFromChat = activityFromChat.start

    const openAttachment = useCallback(
        (message: UIConversationMessage, messages: UIConversationMessage[]) => {
            if (message.attachment == null || message.attachment.kind === "voice") return
            if (onOpenAttachment != null) {
                onOpenAttachment(message, messages)
                return
            }
            // Un envoi d'ici n'a pas (encore) d'activité : la visionneuse, sans aller-retour.
            if (message.id.startsWith(LOCAL_PREFIX)) {
                const viewer = viewerFor(message, messages)
                if (viewer != null) dispatchUi({ type: "OPEN_VIEWER", viewer })
                return
            }
            openingRef.current = { message, messages }
            startActivityFromChat({ id: message.id })
        },
        [startActivityFromChat, onOpenAttachment]
    )

    const actions = useMemo(
        () => ({
            changeDraft: (draft: string) => dispatchUi({ type: "CHANGE_DRAFT", text: draft }),
            send,
            sendHeldFile,
            retry,
            attach: (source: ComposerAttachSource) => void attach(source),
            openAttachment,
            openCopy: (message: UIConversationMessage) => dispatchUi({ type: "OPEN_COPY", message }),
            closeCopy: () => dispatchUi({ type: "CLOSE_COPY" }),
            copy: () => {
                const copied = state.copyMessage?.text
                if (copied) void Clipboard.setStringAsync(copied)
            },
            closeViewer: () => dispatchUi({ type: "CLOSE_VIEWER" }),
            hideUnsent: () => dispatchUi({ type: "HIDE_UNSENT" }),
            closeRecorder: () => dispatchUi({ type: "CLOSE_RECORDER" }),
            pressLink: (link: UIMessageLink) => {
                const href = linkHref(link)
                if (href != null) openConversationLink(href, link.kind)
                else dispatchUi({ type: "OPEN_PHONE", phone: link.value })
            },
            choosePhone: (action: PhoneAction) => {
                if (state.phone != null) openConversationLink(phoneHref(state.phone, action), "phone")
            },
            closePhone: () => dispatchUi({ type: "CLOSE_PHONE" }),
        }),
        [send, sendHeldFile, retry, attach, openAttachment, state.copyMessage, state.phone]
    )

    return {
        state,
        actions,
        sending: text.busy,
        attaching: image.busy || document.busy,
    }
}
